import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../lib/store';
import { Loader2, CheckCircle, Phone, X } from 'lucide-react';

export default function PaymentPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthStore();
  const userId = location.state?.userId || user?.id;

  const [step, setStep] = useState<'phone' | 'processing' | 'success'>('phone');
  const [phone, setPhone] = useState('');
  const [orderId, setOrderId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!userId) {
      navigate('/signup');
    }
  }, [userId]);

  const handleInitiatePayment = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch('https://clipgov-api.dicksonmushif-24d.workers.dev/api/payment/initiate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('jwt_token') || ''}`
        },
        body: JSON.stringify({
          userId,
          phone,
          amount: 5000
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Payment initiation failed');
      }

      setOrderId(result.orderId);
      setStep('processing');
      
      // Start checking payment status
      checkPaymentStatus(result.orderId);
    } catch (err: any) {
      setError(err.message || 'Hitilafu imetokea');
    } finally {
      setLoading(false);
    }
  };

  const checkPaymentStatus = async (orderId: string) => {
    let attempts = 0;
    const maxAttempts = 60; // Check for 2 minutes (every 2 seconds)

    const interval = setInterval(async () => {
      attempts++;
      
      try {
        const response = await fetch(`https://clipgov-api.dicksonmushif-24d.workers.dev/api/payment/status/${orderId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('jwt_token') || ''}`
          }
        });

        const result = await response.json();

        if (result.status === 'completed') {
          clearInterval(interval);
          setStep('success');
          setTimeout(() => {
            navigate('/login');
          }, 3000);
        } else if (result.status === 'failed') {
          clearInterval(interval);
          setError('Malipo yameshindwa. Tafadhali jaribu tena.');
          setStep('phone');
        }
      } catch (err) {
        console.error('Error checking status:', err);
      }

      if (attempts >= maxAttempts) {
        clearInterval(interval);
        setError('Muda umekisha. Tafadhali angalia simu yako na jaribu tena.');
        setStep('phone');
      }
    }, 2000);
  };

  if (step === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-green-100">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md text-center">
          <CheckCircle className="w-20 h-20 text-green-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-900 mb-2">
            Malipo Yamekubaliwa!
          </h2>
          <p className="text-slate-600 mb-4">
            Utaendelea kutumia ClipGov kwa masaa 24.
          </p>
          <p className="text-sm text-slate-500">
            Inakupeleka kwenye login page...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-purple-50 py-12 px-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            Lipia TSh 5,000
          </h1>
          <p className="text-slate-500 mt-2">Kwa masaa 24 ya kutumia ClipGov</p>
        </div>

        {step === 'phone' && (
          <form onSubmit={handleInitiatePayment} className="space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-blue-900 mb-2">Maelekezo:</h3>
              <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                <li>Ingiza namba yako ya simu</li>
                <li>Utapokea USSD push kwenye simu yako</li>
                <li>Ingiza PIN ya M-Pesa/Tigo Pesa</li>
                <li>Malipo yataconfirm automatically</li>
              </ol>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                <Phone className="inline w-4 h-4 mr-1" />
                Namba ya Simu
              </label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="w-full px-4 py-2 bg-white text-slate-900 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder:text-slate-400"
                placeholder="0712345678"
                required
                minLength={10}
              />
              <p className="text-xs text-slate-500 mt-1">
                Namba inayotumika kwa M-Pesa/Tigo Pesa
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Kiasi (TSh)
              </label>
              <input
                type="text"
                value="5,000"
                className="w-full px-4 py-2 bg-slate-50 text-slate-900 border border-slate-300 rounded-lg"
                readOnly
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-4 rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2 font-semibold"
            >
              {loading ? (
                <>
                  <Loader2 size={20} className="animate-spin" />
                  Inatuma USSD...
                </>
              ) : (
                'Anza Malipo'
              )}
            </button>
          </form>
        )}

        {step === 'processing' && (
          <div className="text-center py-8">
            <Loader2 size={48} className="animate-spin text-blue-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">
              Subiri Malipo Yanathibitishwa...
            </h3>
            <p className="text-slate-600 mb-4">
              Angalia simu yako na ingiza PIN ya M-Pesa/Tigo Pesa
            </p>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm text-yellow-800">
              <strong>Order ID:</strong> {orderId}
            </div>
            <p className="text-xs text-slate-500 mt-4">
              Inacheza status... Usifunge page hii
            </p>
          </div>
        )}

        <div className="mt-6 text-center space-y-3">
          <button
            onClick={() => navigate('/')}
            className="text-sm text-slate-500 hover:text-blue-600"
          >
            Rudi nyuma
          </button>
          
          <div className="border-t border-slate-200 pt-4">
            <button
              onClick={() => navigate('/')}
              className="text-sm text-slate-400 hover:text-slate-600 flex items-center justify-center gap-1"
            >
              <X size={14} />
              Skip kwa sasa (Features zitakuwa locked)
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
